import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import withAuth from '../../auth';
import Pagination from '../Pagination';
import moment from 'moment';
import { API_URL, WHITELABEL_ID } from '../../constants';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';

const InvoiceList = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filters, setFilters] = useState({});
    const navigate = useNavigate();
    const { token, setToken } = useContext(AuthContext);

    useEffect(() => {
        fetchInvoices();
    }, [currentPage, sortBy, sortOrder]);

    const fetchInvoices = async (newFilters = null) => {
        setLoading(true);

        try {
            const response = await axios.post(
                `${API_URL}/api/wl/${WHITELABEL_ID}/manager/invoices`,
                {
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    page: currentPage,
                    ...(newFilters || filters),
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setInvoices(response.data.data || []);
            setTotalPages(response.data.last_page || 1);
        } catch (error) {
            console.error('There was an error fetching invoices!', error);

            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSortChange = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }

        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFilterChange = (e, column) => {
        const newFilters = {
            ...filters,
            [column]: e.target.value,
        };

        const nonEmptyFilters = Object.keys(newFilters).reduce((acc, key) => {
            if (
                newFilters[key] !== '' &&
                newFilters[key] !== null &&
                newFilters[key] !== undefined
            ) {
                acc[key] = newFilters[key];
            }
            return acc;
        }, {});

        setFilters(nonEmptyFilters);
    };

    const handleSearchSubmit = () => {
        setCurrentPage(1);
        fetchInvoices();
    };

    const handleDropdownChange = (e, column) => {
        const newFilters = {
            ...filters,
            [column]: e.target.value,
        };

        const nonEmptyFilters = Object.keys(newFilters).reduce((acc, key) => {
            if (
                newFilters[key] !== '' &&
                newFilters[key] !== null &&
                newFilters[key] !== undefined
            ) {
                acc[key] = newFilters[key];
            }
            return acc;
        }, {});

        setFilters(nonEmptyFilters);
        setCurrentPage(1);
        fetchInvoices(nonEmptyFilters);
    };

    const statusColors = {
        open: '#FFF4CC',
        paid: '#D1F2CF',
        void: '#FFD6D6',
    };

    const formatBillingPeriod = (invoice) => {
        if (!invoice.billing_period_start || !invoice.billing_period_end) return '—';

        return `${moment(invoice.billing_period_start).format('YYYY-MM-DD')} → ${moment(
            invoice.billing_period_end
        ).format('YYYY-MM-DD')}`;
    };

    const openPdf = (invoice) => {
        const pdfUrl = invoice.pdf_url || invoice.invoice_pdf_url || invoice.pdfPath;

        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
            return;
        }

        alert('PDF not found for this invoice.');
    };

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Invoice List</h2>

            {loading ? (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: '80vh' }}
                >
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-striped text-center text-nowrap">
                        <thead>
                        <tr>
                            <th onClick={() => handleSortChange('invoice_number')}>
                                Invoice #{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'invoice_number'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th onClick={() => handleSortChange('user_id')}>
                                Customer{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'user_id'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th onClick={() => handleSortChange('status')}>
                                Status{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'status'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th onClick={() => handleSortChange('issue_date')}>
                                Issue Date{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'issue_date'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th onClick={() => handleSortChange('due_date')}>
                                Due Date{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'due_date'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th>Billing Period</th>

                            <th onClick={() => handleSortChange('total')}>
                                Total{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'total'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th onClick={() => handleSortChange('items_count')}>
                                Pickups{' '}
                                <i
                                    className={`fa fa-sort${
                                        sortBy === 'items_count'
                                            ? sortOrder === 'asc'
                                                ? '-asc'
                                                : '-desc'
                                            : ''
                                    }`}
                                />
                            </th>

                            <th>PDF</th>
                        </tr>

                        <tr>
                            <td>
                                <div className="input-group">
                                    <input
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filters.invoice_number || ''}
                                        placeholder="Invoice #"
                                        onChange={(e) => handleFilterChange(e, 'invoice_number')}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') handleSearchSubmit();
                                        }}
                                    />
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSearchSubmit}
                                    >
                                        <i className="fa fa-search" />
                                    </button>
                                </div>
                            </td>

                            <td>
                                <div className="input-group">
                                    <input
                                        className="form-control form-control-sm text-center"
                                        type="text"
                                        inputMode="search"
                                        value={filters.user_id || ''}
                                        placeholder="Customer ID"
                                        onChange={(e) => handleFilterChange(e, 'user_id')}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') handleSearchSubmit();
                                        }}
                                    />
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={handleSearchSubmit}
                                    >
                                        <i className="fa fa-search" />
                                    </button>
                                </div>
                            </td>

                            <td>
                                <div className="input-group">
                                    <select
                                        className="form-control form-control-sm text-center"
                                        value={filters.status || ''}
                                        onChange={(e) => handleDropdownChange(e, 'status')}
                                    >
                                        <option value="">All</option>
                                        <option value="open">Open</option>
                                        <option value="paid">Paid</option>
                                        <option value="void">Void</option>
                                    </select>
                                </div>
                            </td>

                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                        </tr>
                        </thead>

                        <tbody className="text-wrap">
                        {invoices.map((invoice) => (
                            <tr key={invoice.id}>
                                {/*<td>*/}
                                {/*    <Link to={`/invoices/${invoice.id}`}>{invoice.id}</Link>*/}
                                {/*</td>*/}

                                <td>
                                    <Link to={`/invoices/${invoice.id}`}>
                                        {invoice.invoice_number}
                                    </Link>
                                </td>

                                <td>
                                    {invoice.user && (
                                        <Link to={`/users/${invoice.user.id}`}>
                                            {invoice.user.id}
                                            <br />
                                            {invoice.user.full_name}
                                        </Link>
                                    )}
                                </td>

                                <td
                                    style={{
                                        backgroundColor:
                                            statusColors[invoice.status] || 'transparent',
                                    }}
                                >
                                    {invoice.status}
                                </td>

                                <td>
                                    {invoice.issue_date
                                        ? moment(invoice.issue_date).format('YYYY-MM-DD')
                                        : '—'}
                                </td>

                                <td>
                                    {invoice.due_date
                                        ? moment(invoice.due_date).format('YYYY-MM-DD')
                                        : '—'}
                                </td>

                                <td>{formatBillingPeriod(invoice)}</td>

                                <td>
                                    {invoice.currency || 'CAD'} {invoice.total}
                                </td>

                                <td>{invoice.items_count ?? invoice.pickup_count ?? '—'}</td>

                                <td>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => openPdf(invoice)}
                                    >
                                        PDF
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && currentPage <= totalPages && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default withAuth(InvoiceList);
